import { useParams } from 'next/navigation';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import toAccountUrl from 'helpers/account/toAccountUrl';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import mapCosts from 'helpers/utils/mapCosts';
import { Order } from 'types/entity/order';

interface Props {
  order?: Order;
}

const OrderItem: React.FC<Props> = ({ order }) => {
  const { currency } = useI18n();
  const { locale } = useParams();

  const { formatMessage } = useFormat({ name: 'gruenland' });
  const { total: getTotal } = mapCosts({ cart: order, currency });

  const orderDate = order?.createdAt && new Date(order?.createdAt).toISOString().split('T')[0];

  return (
    <div className="mb-6 w-full rounded-md border-[1.5px] border-neutral-300">
      <div className="grid grid-cols-1 items-center rounded-t-md bg-neutral-100 p-3 md:grid-cols-3 md:p-4 lg:grid-cols-3 lg:px-6 lg:py-8">
        <div className="flex-col">
          <div className="flex whitespace-nowrap pb-4">
            <Typography className="text-[14px] font-medium text-primary-black lg:text-[16px]">
              {formatMessage({ id: 'order.number', defaultMessage: 'Numero ordine' })}
            </Typography>
            <Typography
              className="truncate pl-[5px] text-[14px] text-primary-black lg:text-[16px]"
              title={order?.orderNumber}
            >
              {order?.orderNumber}
            </Typography>
          </div>

          <div className="flex">
            <Typography className="text-[14px] font-medium text-primary-black md:hidden">
              {formatMessage({ id: 'order.date', defaultMessage: 'Data ordine' })}
            </Typography>
            <Typography className="pl-[5px] text-[14px] text-primary-black md:pl-0 md:text-secondary-black">
              {orderDate}
            </Typography>
          </div>
        </div>

        <div className="ml-12 hidden md:flex md:flex-col">
          <Typography className="pb-[15px] font-medium text-primary-black">
            {formatMessage({ id: 'summary.total', defaultMessage: 'Totale' })}
          </Typography>
          <Typography className="text-[14px] text-secondary-black">
            {CurrencyHelpers.formatForCurrency(getTotal, locale)}
          </Typography>
        </div>

        <div className="hidden md:flex md:flex-col">
          <Typography className="pb-[15px] font-medium text-primary-black">
            {formatMessage({ id: 'order.status', defaultMessage: 'Stato ordine' })}
          </Typography>
          <Typography className="text-[14px] text-secondary-black">
            {formatMessage({ id: `order.status.${order?.orderState as string}`, defaultMessage: order?.orderState })}
          </Typography>
        </div>
      </div>
      <Link link={toAccountUrl(`?hash=orders&id=order_${order?.orderId}`)}>
        <div className="flex w-full cursor-pointer items-center justify-between px-3 py-4 md:px-4 lg:px-6 lg:py-5">
          <div className="flex">
            <Typography className="text-[14px] text-primary-black">{order?.lineItems?.length.toString()}</Typography>
            <Typography className="pl-2 text-[14px] text-primary-black">
              {formatMessage({ id: 'order.articles', defaultMessage: 'Articoli' })}
            </Typography>
          </div>

          <ChevronRightIcon strokeWidth={1.5} className="w-6 text-secondary-black" />
        </div>
      </Link>
    </div>
  );
};

export default OrderItem;
